/**
 * Online MethodSCRIPT Editor - Main Entry Point
 *
 * This file initializes the Monaco editor with MethodScript language support.
 * It uses AMD/RequireJS for dynamic Monaco loading which TypeScript doesn't understand,
 * so type checking is disabled for the AMD-specific sections.
 */

// Re-export MessageType for testing
import { MessageType } from 'methodscript-device';
import { logger } from 'shared-monaco-methodscript';

import { createCodeActionProvider, createLanguageCompletionProvider, registerMethodScriptQuickFixWidget } from '../public/shared-monaco/index.js';
import { languageConfig, monarchTokensProvider, methodscriptTheme } from '../public/shared-monaco/monaco-language-config.js';

// Device communication
import { initializeDeviceCommunication, parseAndDisplayDataPackages, type DeviceManagers } from './device-communication-setup.js';
import { MethodScriptFormatterService } from './formatter.js';
import { webEditorLanguageService } from './language-service-bridge.js';
// Monaco providers
import { createMethodScriptHoverProvider, createMethodScriptSignatureHelpProvider, createMethodScriptFoldingRangeProvider } from './monaco-providers.js';
// URL utilities
import { loadScriptFromQuerystring, loadScriptFromHash, createShareableUrl } from './script-url-utils.js';
// Validation
import { validateCode, getCurrentDiagnostics } from './validation.js';

/** Debounce delay (ms) for auto-validation after editor content changes */
const VALIDATION_DEBOUNCE_MS = 500;

/** Duration (ms) to show the "Copied!" feedback on the share button */
const SHARE_BUTTON_FEEDBACK_MS = 2000;

// @ts-expect-error - LZ-string wrapper is a vendor file without types
import LZStringImport from '../public/vendor/lz-string-wrapper.js';

// LZString for window exposure (debugging/testing)
import type { LZStringStatic } from './lz-string.js';
const LZString = LZStringImport as LZStringStatic;

// Monaco types (will be available after AMD load)
import type * as monacoTypes from 'monaco-editor';

// Global declarations for AMD loader and Monaco
/* eslint-disable typescript/no-explicit-any */
declare const require: any;
declare const monaco: typeof monacoTypes;
declare global {
    interface Window {
        LZString: LZStringStatic;
        editor: monacoTypes.editor.IStandaloneCodeEditor;
        validateCode: () => Promise<void>;
        // eslint-disable-next-line typescript/no-explicit-any
        webserialManager: unknown;
        webbleManager: unknown;
        communicationPanel: unknown;
        testCodeActionProvider: unknown;
        parseAndDisplayDataPackages: unknown;
        MessageType: unknown;
    }
}
/* eslint-enable typescript/no-explicit-any */

// Module state
let editor: monacoTypes.editor.IStandaloneCodeEditor;
let formatterService: MethodScriptFormatterService;
let deviceManagers: DeviceManagers | null = null;

// Expose LZString to window for debugging/testing
if (typeof window !== 'undefined') {
    window.LZString = LZString;
}

/**
 * Update status display
 */
function updateStatus(message: string, isError: boolean): void {
    const statusEl = document.getElementById('status');
    if (statusEl) {
        statusEl.textContent = message;
        statusEl.className = isError ? 'error' : 'success';
    }
}

/**
 * Update editor content with new script
 */
function updateEditorContent(script: string): boolean {
    if (!editor || !script) {
        return false;
    }

    editor.setValue(script);
    updateStatus('Script loaded from URL', false);
    return true;
}

/**
 * Share the current script by creating a shareable URL
 */
function shareScript(): void {
    const code = editor.getValue();

    if (!code || code.trim() === '') {
        updateStatus('Nothing to share - editor is empty', true);
        return;
    }

    try {
        const shareUrl = createShareableUrl(code);

        // Copy to clipboard
        navigator.clipboard
            .writeText(shareUrl)
            .then(() => {
                // Visual feedback: change button appearance
                const shareBtn = document.getElementById('shareBtn');
                if (shareBtn) {
                    const originalText = shareBtn.textContent;
                    shareBtn.textContent = 'Copied!';
                    shareBtn.classList.add('copied');

                    updateStatus('Link copied to clipboard!', false);

                    // Reset button after feedback duration
                    setTimeout(() => {
                        shareBtn.textContent = originalText;
                        shareBtn.classList.remove('copied');
                    }, SHARE_BUTTON_FEEDBACK_MS);
                }
            })
            .catch((err) => {
                logger.error('Failed to copy to clipboard:', err);
                updateStatus('Failed to copy link - please copy manually', true);

                // Fallback: show the URL in a prompt
                prompt('Copy this shareable link:', shareUrl);
            });
    } catch (error) {
        logger.error('Failed to create share link:', error);
        updateStatus('Failed to create share link', true);
    }
}

/**
 * Set up button event handlers
 */
function setupButtonHandlers(): void {
    document.getElementById('formatBtn')?.addEventListener('click', () => {
        editor.getAction('editor.action.formatDocument')?.run();
    });

    document.getElementById('clearBtn')?.addEventListener('click', () => {
        editor.setValue('');
    });

    document.getElementById('shareBtn')?.addEventListener('click', () => {
        shareScript();
    });
}

/**
 * Initialize the Monaco editor and all language features
 */
function initializeEditor(monacoInstance: typeof monacoTypes): void {
    // Register MethodScript language
    monacoInstance.languages.register({ id: 'methodscript' });

    // Set language configuration
    monacoInstance.languages.setLanguageConfiguration('methodscript', languageConfig);

    // Set Monarch tokenizer
    // eslint-disable-next-line typescript/no-explicit-any
    monacoInstance.languages.setMonarchTokensProvider('methodscript', monarchTokensProvider as any);

    // Define theme
    monacoInstance.editor.defineTheme('methodscript-dark', methodscriptTheme);

    // Create editor
    const editorContainer = document.getElementById('editor');
    if (!editorContainer) {
        throw new Error('Editor container not found');
    }

    editor = monacoInstance.editor.create(editorContainer, {
        value: '',
        language: 'methodscript',
        theme: 'methodscript-dark',
        minimap: { enabled: true },
        fontSize: 14,
        lineNumbers: 'on',
        roundedSelection: false,
        scrollBeyondLastLine: false,
        automaticLayout: true,
        suggest: {
            showWords: false, // Disable word-based completions from open editors
            showInlineDetails: true, // Show details inline for all items
        },
        lightbulb: {
            enabled: 'off' as monacoTypes.editor.ShowLightbulbIconMode,
        },
    });

    // Register completion provider backed by the C# engine
    monacoInstance.languages.registerCompletionItemProvider(
        'methodscript',
        createLanguageCompletionProvider(monacoInstance.languages, webEditorLanguageService),
    );

    // Register code action provider for quick fixes using shared provider
    const codeActionProvider = createCodeActionProvider(() => getCurrentDiagnostics(), monacoInstance);
    monacoInstance.languages.registerCodeActionProvider('methodscript', codeActionProvider);
    const quickFixWidget = registerMethodScriptQuickFixWidget(editor, () => getCurrentDiagnostics(), monacoInstance);

    // Expose for testing
    window.testCodeActionProvider = codeActionProvider;

    // Register hover provider
    monacoInstance.languages.registerHoverProvider('methodscript', createMethodScriptHoverProvider(monacoInstance));

    // Register signature help provider
    monacoInstance.languages.registerSignatureHelpProvider('methodscript', createMethodScriptSignatureHelpProvider());

    // Register folding range provider
    monacoInstance.languages.registerFoldingRangeProvider('methodscript', createMethodScriptFoldingRangeProvider(monacoInstance));

    // Register formatting provider
    monacoInstance.languages.registerDocumentFormattingEditProvider('methodscript', {
        provideDocumentFormattingEdits: async (model) => {
            const text = model.getValue();

            // Use the formatter service (calls C# via LSP or WASM)
            if (!formatterService) {
                formatterService = new MethodScriptFormatterService();
            }

            const formattedText = await formatterService.formatDocument(text);

            // Return a single edit that replaces the entire document
            if (formattedText !== text) {
                const lastLine = model.getLineCount();
                const lastColumn = model.getLineMaxColumn(lastLine);

                return [
                    {
                        range: new monacoInstance.Range(1, 1, lastLine, lastColumn),
                        text: formattedText,
                    },
                ];
            }

            return [];
        },
    });

    // Add keyboard shortcut for formatting (Ctrl+Shift+F)
    editor.addCommand(monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyMod.Shift | monacoInstance.KeyCode.KeyF, () => {
        editor.getAction('editor.action.formatDocument')?.run();
    });

    // Add keyboard shortcut for formatting (Ctrl+S)
    editor.addCommand(monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyS, () => {
        editor.getAction('editor.action.formatDocument')?.run();
    });

    // Create validation callback
    const validateCallback = async () => {
        await validateCode(editor, monacoInstance, updateStatus);
        quickFixWidget.update();
    };

    // Debounced auto-validation
    let validationTimeout: ReturnType<typeof setTimeout>;
    editor.onDidChangeModelContent(() => {
        clearTimeout(validationTimeout);
        validationTimeout = setTimeout(() => {
            validateCallback();
        }, VALIDATION_DEBOUNCE_MS);
    });

    // Set up button handlers
    setupButtonHandlers();

    // Load script from querystring or hash if present
    const scriptFromUrl = loadScriptFromQuerystring();
    const scriptFromHash = loadScriptFromHash();

    if (scriptFromUrl) {
        updateEditorContent(scriptFromUrl);
        // Don't call validateCode() here - if loading succeeded, the loaded script
        // will be validated automatically via onDidChangeModelContent.
        // If validation failed, we want to keep the validation error message visible.
    } else if (scriptFromHash) {
        // Load from hash on initial load (for bookmark/direct navigation)
        updateEditorContent(scriptFromHash);
    } else {
        // Initial validation only if no script from URL or hash
        validateCallback();
    }

    // Initialize device communication
    deviceManagers = initializeDeviceCommunication(editor, monacoInstance, updateStatus);

    // Listen for hashchange events to support loading scripts without page reload
    window.addEventListener('hashchange', () => {
        const newScriptFromHash = loadScriptFromHash();
        if (newScriptFromHash) {
            updateEditorContent(newScriptFromHash);
        }
    });

    // Expose globals for testing
    window.editor = editor;
    window.validateCode = validateCallback;
    window.currentDiagnostics = getCurrentDiagnostics();

    // Expose device communication for tests
    const activeDeviceManagers = deviceManagers;
    if (activeDeviceManagers) {
        window.webserialManager = activeDeviceManagers.webserialManager;
        window.webbleManager = activeDeviceManagers.webbleManager;
        window.communicationPanel = activeDeviceManagers.communicationPanel;

        // Expose parsing functions and MessageType for testing
        window.parseAndDisplayDataPackages = (data: string) => {
            parseAndDisplayDataPackages(data, activeDeviceManagers.packageParser, activeDeviceManagers.communicationPanel);
        };
        window.MessageType = MessageType;
    }
}

// Load Monaco Editor using AMD/RequireJS
require.config({ paths: { vs: '/monaco-editor/min/vs' } });

require(['vs/editor/editor.main'], function () {
    // Monaco is now available as global 'monaco'
    initializeEditor(monaco);
});
